'use client';

import deepmerge from 'deepmerge';
import { createContext, useContext, useMemo } from 'react';
import type { DeepPartial } from '~/types';

export type GlobalContextType = {
  hasUtmContentId: boolean;
  semTitle?: string;
};

const defaultContext: GlobalContextType = {
  hasUtmContentId: false,
  semTitle: undefined
};

export const GlobalContext = createContext<GlobalContextType>(defaultContext);

type GlobalContextProviderProps = GlobalContextType & {
  children: React.ReactNode;
};

export function GlobalContextProvider(props: GlobalContextProviderProps) {
  const value = useMemo<GlobalContextType>(
    () => ({
      hasUtmContentId: props.hasUtmContentId,
      semTitle: props.semTitle
    }),
    [props.hasUtmContentId, props.semTitle]
  );

  return (
    <GlobalContext.Provider value={value}>
      {props.children}
    </GlobalContext.Provider>
  );
}

export function useSemTitle(defaultValue?: string): string | undefined {
  return useContext(GlobalContext).semTitle ?? defaultValue;
}

export const generateGlobalContext = (
  customState: DeepPartial<GlobalContextType>
): GlobalContextType =>
  deepmerge<GlobalContextType, DeepPartial<GlobalContextType>>(
    defaultContext,
    customState
  );

export function MockedGlobalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<GlobalContextType> }>) {
  return (
    <GlobalContext.Provider value={generateGlobalContext(mockValue)}>
      {children}
    </GlobalContext.Provider>
  );
}
